import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Box, Button, CircularProgress } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { approveSuratJalanApi, verifySuratJalanApi } from "../../api/suratJalanApi";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { SuratJalanListItem } from "../../types/suratJalan";
import type { User } from "../../types/user";
import { getErrorMessage } from "../../utils/errorHandling";
import { getSuratJalanActionAvailability } from "../../utils/suratJalanActions";
import { toastEmitter } from "../../utils/toastEmitter";
import SuratJalanRequestRevisionDialog from "./SuratJalanRequestRevisionDialog";

type TranslationKeys = keyof typeof translations.en;

interface SuratJalanRowActionsProps {
  row: SuratJalanListItem;
  user: User | null;
  onActionComplete: () => void;
}

const SuratJalanRowActions: React.FC<SuratJalanRowActionsProps> = ({
  row,
  user,
  onActionComplete,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const { canVerify, canApprove, canRevise } = getSuratJalanActionAvailability(user, row.status);

  if (!canVerify && !canApprove && !canRevise) {
    return null;
  }

  const runAction = async (action: () => Promise<unknown>, successKey: TranslationKeys) => {
    setLoading(true);
    try {
      await action();
      toastEmitter.emit(t(successKey), "success");
      onActionComplete();
    } catch (err) {
      toastEmitter.emit(getErrorMessage(err, t, "suratJalanAction"), "error");
    } finally {
      setLoading(false);
    }
  };

  const stopRowNavigation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box
      sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}
      onClick={stopRowNavigation}
    >
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          {canVerify && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon fontSize="small" />}
              disabled={loading}
              data-testid={TestId.SURAT_JALAN.ROW_VERIFY(row._id)}
              onClick={(event) => {
                stopRowNavigation(event);
                void runAction(() => verifySuratJalanApi(row._id), "suratJalan.verified");
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("suratJalan.verify" as TranslationKeys)}
            </Button>
          )}
          {canApprove && (
            <Button
              size="small"
              variant="contained"
              startIcon={<TaskAltIcon fontSize="small" />}
              disabled={loading}
              data-testid={TestId.SURAT_JALAN.ROW_APPROVE(row._id)}
              onClick={(event) => {
                stopRowNavigation(event);
                void runAction(() => approveSuratJalanApi(row._id), "suratJalan.approved");
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("suratJalan.approve" as TranslationKeys)}
            </Button>
          )}
          {canRevise && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ReplayOutlinedIcon fontSize="small" />}
              disabled={loading}
              data-testid={TestId.SURAT_JALAN.ROW_REQUEST_REVISION(row._id)}
              onClick={(event) => {
                stopRowNavigation(event);
                setRevisionOpen(true);
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("orders.requestRevision" as TranslationKeys)}
            </Button>
          )}
        </>
      )}

      <SuratJalanRequestRevisionDialog
        suratJalanId={row._id}
        open={revisionOpen}
        onClose={() => setRevisionOpen(false)}
        onSuccess={() => {
          onActionComplete();
        }}
      />
    </Box>
  );
};

export default SuratJalanRowActions;
